/**
 * ChangeSectionConfirmDialog — Sprint P-A1
 *
 * Shown when the buyer has picked seats and tries to switch to a different
 * section. Confirming releases their in-session seat holds via the same
 * atomic reserve-with-empty-seats path used across the app.
 */
import React from "react";
import { AlertTriangle, X } from "lucide-react";

export default function ChangeSectionConfirmDialog({
  open,
  currentSection,
  targetSection,
  pickCount,
  onCancel,
  onConfirm,
  busy = false,
}) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Change section confirmation"
      data-testid="change-section-confirm"
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-3"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onCancel}
        className="absolute inset-0 bg-black/50"
        data-testid="change-section-backdrop"
      />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-lg sm:rounded-lg p-5 shadow-xl">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-sm bg-amber-100 flex items-center justify-center shrink-0">
            <AlertTriangle size={16} className="text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg font-bold tracking-tight">Change section?</h3>
            <p className="text-sm text-[var(--gt-ink-2)] mt-1">
              You have <strong>{pickCount}</strong> seat{pickCount === 1 ? "" : "s"} selected in{" "}
              <strong>{currentSection}</strong>. Switching to <strong>{targetSection}</strong>{" "}
              will release your selected seats.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="w-8 h-8 rounded-sm border border-[var(--gt-line)] flex items-center justify-center hover:bg-[var(--gt-bg-surface)] shrink-0"
            data-testid="change-section-close-btn"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onCancel}
            className="gt-btn-ghost text-xs"
            data-testid="change-section-cancel-btn"
          >
            Keep current seats
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="gt-btn-primary text-xs"
            data-testid="change-section-confirm-btn"
          >
            {busy ? "Releasing…" : "Change section"}
          </button>
        </div>
      </div>
    </div>
  );
}
